import { IAppState, IMembers } from '../../types/state.d';
import { Page, PageInfo, PageType } from '../../../types/graphql';
import {
  getMembers,
  getPageInfo,
  getTeamGuid,
} from '../../selectors/apiSelectors';

import { ERROR_MESSAGES } from '../../consts';
import apolloClient from '../../apolloClient';
import { gql } from '@apollo/client';

export type MemberListItem = {
  numberOfMembers: number;
  members: IMembers;
  pageInfo: PageInfo;
};

export type MemberItem = {
  userGuid: string;
  firstName: string | null;
  lastName: string | null;
  profileImage: string | null;
  fundraisingPageGuid: string;
  fundraisingPageShortName: string;
  fundraisingPageName: string;
  pageUrl: string;
  pageType: PageType;
};

const toMemberItem = (edge: any): MemberItem => {
  let node: Page = edge.node;
  const firstName = node.owner?.name?.split(' ').slice(0, -1).join(' ') ?? '';
  const lastName = node.owner?.name?.split(' ').slice(-1)[0] ?? '';

  return {
    userGuid: node.owner?.legacyId ?? '',
    firstName,
    lastName,
    profileImage: node.owner?.avatar ?? '',
    fundraisingPageGuid: node.legacyId,
    fundraisingPageShortName: node.slug ?? '',
    fundraisingPageName: node.title,
    pageUrl: node.url,
    pageType: node.type,
  };
};

const fetchMembersListLeaderboards = async (
  getState: IAppState,
): Promise<MemberListItem> => {
  const pageType = PageType.Team;
  const teamGuid = await getTeamGuid(getState);
  const pageInfoData = await getPageInfo(getState);
  const members = await getMembers(getState);

  const { data, error } = await apolloClient.query({
    query: gql`
      query LeaderboardMemberList(
        $after: String
        $type: PageType!
        $teamGuid: ID!
      ) {
        page(type: $type, id: $teamGuid) {
          id
          legacyId
          allowFundraising
          membershipPolicy
          fundraisers: leaderboard(type: FUNDRAISERS, first: 5, after: $after) {
            edges {
              node {
                title
                status
                legacyId
                id
                url
                slug
                type
                owner {
                  id
                  avatar
                  name
                }
                targetWithCurrency {
                  value
                }
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
            totalCount
          }
        }
      }
    `,
    variables: {
      type: pageType,
      teamGuid: teamGuid,
      after: pageInfoData?.endCursor,
    },
  });

  const numberOfMembers = data?.page?.fundraisers?.totalCount ?? 0;
  const pageInfo = data?.page?.fundraisers?.pageInfo;

  if (error) {
    throw new Error(ERROR_MESSAGES.GET_MEMBERS_LIST);
  }

  const mappedMembers = data?.page?.fundraisers?.edges
    ? data.page.fundraisers.edges.map(toMemberItem)
    : [];

  return {
    numberOfMembers,
    members: [...members, ...mappedMembers],
    pageInfo,
  };
};

export default fetchMembersListLeaderboards;
